// Copied from apps/admin/src/features/map-view/components/PickupGeofences.tsx (PRD #875).
import React, { useMemo } from "react";
import L, { type LeafletEventHandlerFnMap } from "leaflet";
import { Marker, Polygon, Tooltip, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import {
  GEOFENCE_CLASS,
  CLUSTER_MAX_ZOOM,
  GEOFENCE_MIN_ZOOM,
  isInViewport,
  polygonsCenter,
  useGeofenceShapes,
  useMapViewport,
  useZoomLevel,
  type GeofenceItem,
  type GeofenceShape,
} from "../model/pickup-geofence";
import { BoundedCache } from "../model/bounded-cache";

const DEFAULT_GEOFENCE_COLOR = "#007AFF";
const SHAPE_PANE = "pickup-shapes";

const CLUSTER_RING_COLOR = "#007AFF";

/** Ring on a white centre, matching govdash's cluster. Self-contained so the
 *  package's stylesheet need not be imported globally. */
const clusterIcon = (cluster: { getChildCount: () => number }): L.DivIcon => {
  const count = cluster.getChildCount();
  const size = Math.round(Math.min(72, 30 + Math.log10(count + 1) * 12));
  // govdash proportions at size 50: r 18, stroke 5, text 12.
  const radius = size * 0.36;
  const stroke = size * 0.1;
  const centre = size / 2;
  const font = Math.round(size * 0.24);

  return L.divIcon({
    className: "pickup-cluster",
    html:
      `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" ` +
      `style="filter:drop-shadow(0 1px 3px rgba(0,0,0,.1))">` +
      `<circle cx="${centre}" cy="${centre}" r="${radius}" fill="none" ` +
      `stroke="${CLUSTER_RING_COLOR}" stroke-width="${stroke}"/>` +
      `<circle cx="${centre}" cy="${centre}" r="${radius - stroke}" fill="#fff" ` +
      `stroke="#fff" stroke-width="1"/>` +
      `<text x="${centre}" y="${centre}" text-anchor="middle" dominant-baseline="middle" ` +
      `font-weight="bold" font-size="${font}" fill="#1f2937" ` +
      `font-family="system-ui, -apple-system, sans-serif">${count}</text></svg>`,
    iconSize: L.point(size, size, true),
    iconAnchor: L.point(centre, centre),
  });
};

/** Clustering stops before geofences begin, so a cluster never sits on top of
 *  the shapes it stands for. */
const CLUSTER_OPTIONS = {
  chunkedLoading: true,
  maxClusterRadius: 100,
  disableClusteringAtZoom: CLUSTER_MAX_ZOOM,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  iconCreateFunction: clusterIcon,
} as const;

const labelIconCache = new BoundedCache<L.DivIcon>(512);

/** Cached by content: a fresh DivIcon each render makes Leaflet rebuild the element. */
const labelIcon = (text: string, color: string, font: string, colorClass = ""): L.DivIcon => {
  const key = `${text}|${color}|${font}|${colorClass}`;
  const cached = labelIconCache.get(key);
  if (cached) return cached;

  const icon = L.divIcon({
    className: "geofence-label",
    html:
      `<div class="${colorClass}" style="display:flex;align-items:center;justify-content:center;` +
      `min-width:18px;height:18px;padding:0 4px;border-radius:9px;` +
      `background:${color};color:${font};border:1.5px solid #fff;` +
      `font:700 10px/1 Inter,sans-serif;box-sizing:border-box;` +
      `box-shadow:0 1px 2px rgba(0,0,0,.3);">${text}</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
  labelIconCache.set(key, icon);
  return icon;
};

interface Styling<T extends GeofenceItem> {
  renderMarker: (item: T) => React.ReactNode;
  colorFor?: (item: T) => { fill: string; font: string; colorClass?: string };
  labelFor?: (item: T) => string;
  eventHandlersFor?: (item: T) => LeafletEventHandlerFnMap;
}

export interface PickupGeofenceLayerProps<T extends GeofenceItem> extends Styling<T> {
  items: T[];
  /** Group the pins into clusters. Off by default: it hides the stop number and
   *  job-status colour that route maps rely on. */
  cluster?: boolean;
}

/**
 * Geofences for shapes big enough to read and currently on screen. Off-screen
 * ones draw nothing rather than a pin nobody can see.
 */
function ViewportGeofences<T extends GeofenceItem>({
  shapes,
  colorFor,
  labelFor,
  eventHandlersFor,
}: { shapes: GeofenceShape<T>[] } & Omit<Styling<T>, "renderMarker">) {
  const { bounds } = useMapViewport();
  const map = useMap();
  // Under the photo markers (440) as well as the pins, or a photo inside a
  // geofence could not be hovered.
  if (!map.getPane(SHAPE_PANE)) map.createPane(SHAPE_PANE).style.zIndex = "420";

  return (
    <>
      {shapes
        .filter((shape) => isInViewport(shape, bounds))
        .map((shape) => {
          const palette = colorFor?.(shape.item);
          const color = palette?.fill ?? DEFAULT_GEOFENCE_COLOR;
          // currentColor plus the theme class, the pairing the pin already uses,
          // so the two cannot drift when a status colour changes.
          const className = [GEOFENCE_CLASS, palette?.colorClass].filter(Boolean).join(" ");
          const label = labelFor?.(shape.item) ?? "";

          return (
            <React.Fragment key={`geofence-${shape.item.id}`}>
              <Polygon
                pane={SHAPE_PANE}
                positions={shape.polygons}
                pathOptions={{
                  color,
                  weight: 2,
                  fillOpacity: 0.35,
                  className,
                }}
                eventHandlers={eventHandlersFor?.(shape.item)}
              >
                {shape.item.name && (
                  <Tooltip pane="tooltipPane" sticky>
                    {shape.item.name}
                  </Tooltip>
                )}
              </Polygon>
              {label && (
                <Marker
                  position={polygonsCenter(shape.polygons)}
                  icon={labelIcon(label, color, palette?.font ?? "#FFFFFF", palette?.colorClass)}
                  interactive={false}
                />
              )}
            </React.Fragment>
          );
        })}
    </>
  );
}

export function PickupGeofenceLayer<T extends GeofenceItem>({
  items,
  cluster = false,
  ...styling
}: PickupGeofenceLayerProps<T>) {
  const shapes = useGeofenceShapes(items);
  const zoom = useZoomLevel();
  // Keyed on the decision, not the zoom number: zooming 11->12 leaves this
  // false either way, so the pin elements keep their identity and React skips
  // thousands of markers instead of rebuilding them and the cluster index.
  const showShapes = zoom >= GEOFENCE_MIN_ZOOM;

  const { drawn, pinned } = useMemo(() => {
    const drawn: GeofenceShape<T>[] = [];
    const pinned: GeofenceShape<T>[] = [];
    for (const shape of shapes) {
      const asShape = showShapes && shape.polygons.length > 0;
      if (asShape) drawn.push(shape);
      else if (!shape.item.shapeOnly) pinned.push(shape);
    }
    return { drawn, pinned };
  }, [shapes, showShapes]);

  const { renderMarker } = styling;
  const pins = useMemo(
    () =>
      pinned.map((shape) => (
        <React.Fragment key={`pin-${shape.item.id}`}>{renderMarker(shape.item)}</React.Fragment>
      )),
    [pinned, renderMarker],
  );

  return (
    <>
      {cluster ? <MarkerClusterGroup {...CLUSTER_OPTIONS}>{pins}</MarkerClusterGroup> : pins}
      <ViewportGeofences shapes={drawn} {...styling} />
    </>
  );
}
